// ─── 🤖 La porta per Duet (22/10/2026) ───────────────────────────────────────
//
// Mockup «Pacchetto 3 · La porta per Duet» v4, approvato: *«1A 2A 3B»*, *«4A 5A 6A»*, *«7A 8A 9A»*.
// Lo studio e lo stato: `docs/funzioni/analytics-duet.md`.
//   · le fasce di punta, segnate dalla Segreteria (1A: le quattro fasce del bot);
//   · la fotografia del giorno, calcolata la mattina e conservata (2A);
//   · le conversazioni dei soci col bot, che la Segreteria legge in Analytics (3B);
//   · ⭐ le proposte: quello che Duet vuole scrivere a un socio **aspetta chi approva** (6A:
//     l'amministrazione), dal gestionale (4A) o dal suo Telegram coi bottoni (5A, 7A), finché le maglie
//     di quel tipo non vengono allargate;
//   · «🤖 Parla con Duet» dal Telegram di chi approva (9A).
// 8A: quando un socio scrive al bot, risponde il bot come oggi; Duet aggiunge dopo, con una proposta.
import { z } from 'zod';
import { FASCE_DEL_GIORNO } from './preferenze-bot.js';
// ── Le fasce di punta (1A) ───────────────────────────────────────────────────
export const TIPI_DI_FASCIA = ['punta', 'normale', 'tranquilla'];
export const ETICHETTE_TIPO_DI_FASCIA = {
    punta: 'di punta',
    normale: 'normale',
    tranquilla: 'tranquilla',
};
/** I giorni come li conta la settimana italiana: 1 = lunedì … 7 = domenica (ISO). */
export const GIORNI_DELLA_SETTIMANA = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
export const fasciaDiPuntaSchema = z.object({
    weekday: z.number().int().min(1).max(7),
    band: z.enum(FASCE_DEL_GIORNO),
    kind: z.enum(TIPI_DI_FASCIA),
});
/** Tutte le 28 caselle: una fascia senza riga nel database esce «normale». */
export const fasceDiPuntaSchema = z.object({ items: z.array(fasciaDiPuntaSchema) });
/** La fascia di un'ora `HH:MM`: mattina prima delle 12, pranzo 12–15, pomeriggio 15–18, sera dalle 18. */
export function fasciaDellOra(ora) {
    if (ora < '12:00')
        return 'mattina';
    if (ora < '15:00')
        return 'pranzo';
    if (ora < '18:00')
        return 'pomeriggio';
    return 'sera';
}
/** Dove finisce una fascia, in minuti: i buchi si tagliano qui, per dirli fascia per fascia. */
export const CONFINI_DELLE_FASCE = [12 * 60, 15 * 60, 18 * 60];
/** Il giorno della settimana ISO (1 = lunedì) di `AAAA-MM-GG`. */
export function giornoIso(data) {
    const g = new Date(`${data}T12:00:00Z`).getUTCDay();
    return g === 0 ? 7 : g;
}
// ── La fotografia del giorno (2A) ────────────────────────────────────────────
/** Quanti giorni guarda la fotografia: gli stessi in cui i soci prenotano da soli. */
export const GIORNI_DELLA_FOTOGRAFIA = 14;
/** I primi giorni, dove c'è meno tempo per riempire un buco. */
export const GIORNI_VICINI = 3;
/** Un buco più corto di così non è un'ora di gioco: non si conta. */
export const BUCO_MINIMO_MINUTI = 60;
export const fotografiaSchema = z.object({
    date: z.iso.date(),
    madeAt: z.string(),
    buchi: z.object({
        /** Ore libere nei 14 giorni, a mezz'ora. */
        ore: z.number(),
        oreVicine: z.number(),
        orePunta: z.number(),
        finestre: z.array(z.object({
            date: z.iso.date(),
            band: z.enum(FASCE_DEL_GIORNO),
            kind: z.enum(TIPI_DI_FASCIA),
            courtName: z.string(),
            from: z.string(),
            to: z.string(),
        })),
        /** Le fasce vuote quasi sempre nelle ultime quattro settimane (almeno l'80% delle ore libero). */
        quasiSempreVuote: z.array(z.object({ weekday: z.number().int(), band: z.enum(FASCE_DEL_GIORNO), libero: z.number() })),
    }),
    partite: z.array(z.object({
        bookingId: z.uuid(),
        date: z.iso.date(),
        time: z.string(),
        courtName: z.string(),
        /** Col cognome puntato. */
        organizzatore: z.string().nullable(),
        mancano: z.number().int(),
        tipo: z.string().nullable(),
        livello: z.number().nullable(),
        /**
         * 👥 I soci adatti (mockup «I soci adatti a una partita», *«1A 2C 3A 4B 5A»*): i primi 10, in
         * ordine. Alla Segreteria col nome intero; a Duet `nome` è col cognome puntato.
         * Le fotografie di prima non li hanno: valgono vuoti.
         */
        adatti: z
            .array(z.object({
            memberId: z.uuid(),
            nome: z.string(),
            /** Col cognome puntato, come lo vedono gli altri soci. */
            nomePerGliAltri: z.string(),
            livello: z.string().nullable(),
            /** Una riga sul perché è adatto. */
            perche: z.string(),
        }))
            .default([]),
        /** Quanti sono in tutto, anche oltre i 10 dell'elenco. */
        adattiTotali: z.number().int().default(0),
    })),
    lezioni: z.array(z.object({ coachName: z.string(), date: z.iso.date(), times: z.array(z.string()) })),
    persoIeri: z.object({
        ricerche: z.array(z.object({ kind: z.string(), forDate: z.string().nullable(), quante: z.number().int() })),
        rifiuti: z.array(z.object({ action: z.string(), reason: z.string().nullable(), quanti: z.number().int() })),
    }),
    risposte: z
        .object({
        avvisiMandati: z.number().int(),
        avvisiToccati: z.number().int(),
        invitiMandati: z.number().int(),
        invitiAccettati: z.number().int(),
        inLista: z.number().int(),
        entrati: z.number().int(),
    })
        .nullable(),
    bot: z.object({
        totaliArrivati: z.boolean(),
        messaggi: z.number().int(),
        soci: z.number().int(),
    }),
});
// ── Le conversazioni (3B) ────────────────────────────────────────────────────
/** Quanti giorni si tengono: come il bot. */
export const GIORNI_DELLE_CONVERSAZIONI = 90;
/** ⇢ Dal bot, di notte: i messaggi del giorno prima. */
export const conversazioniDelBotSchema = z.object({
    messages: z
        .array(z.object({
        /** Il numero del messaggio nel bot: rimandarlo non lo scrive due volte. */
        id: z.number().int().positive(),
        memberId: z.uuid(),
        saidBy: z.enum(['socio', 'bot']),
        text: z.string().min(1).max(8000),
        at: z.iso.datetime({ offset: true }),
    }))
        .max(20000),
});
/** `tenuti`: arrivati e conservati; `scartati`: di un socio sconosciuto, o scritti prima del suo sì alla privacy. */
export const esitoConversazioniSchema = z.object({
    outcome: z.literal('fatto'),
    tenuti: z.number().int(),
    scartati: z.number().int(),
});
/** Per la Segreteria (3B): un giorno, socio per socio. */
export const conversazioniDelGiornoSchema = z.object({
    date: z.iso.date(),
    soci: z.array(z.object({
        memberId: z.uuid(),
        nome: z.string(),
        messaggi: z.array(z.object({ saidBy: z.enum(['socio', 'bot']), text: z.string(), at: z.string() })),
    })),
});
// ── Le proposte (4A 5A 6A 7A) ────────────────────────────────────────────────
export const TIPI_DI_PROPOSTA = [
    'risposta',
    'informativa',
    'disponibilita',
    'invito',
    'domanda_disdetta',
];
export const ETICHETTE_TIPO_DI_PROPOSTA = {
    risposta: '💬 Risposta a chi scrive',
    informativa: '📢 Informativa',
    disponibilita: '🗓️ Richiesta di disponibilità',
    invito: '🎾 Invito a una partita o a una lezione',
    domanda_disdetta: '❓ Domanda dopo una disdetta',
};
export const STATI_DI_PROPOSTA = ['attesa', 'approvata', 'scartata', 'scaduta', 'mandata_da_sola'];
export const ETICHETTE_STATO_DI_PROPOSTA = {
    attesa: 'in attesa',
    approvata: 'approvata',
    scartata: 'scartata',
    scaduta: 'scaduta',
    mandata_da_sola: 'mandata da sola',
};
/** Una proposta che aspetta troppo non serve più: si chiude da sola. */
export const ORE_DI_ATTESA_MASSIME = 12;
/** ⇢ Da Duet. */
export const nuovaPropostaSchema = z
    .object({
    memberId: z.uuid(),
    kind: z.enum(TIPI_DI_PROPOSTA),
    text: z.string().trim().min(1).max(1500),
    /** Perché la vuole mandare: lo legge chi approva. */
    reason: z.string().trim().min(1).max(600),
    /** Il messaggio del socio a cui risponde (`bot_conversations.id`), per una risposta. */
    inReplyTo: z.number().int().positive().nullable().default(null),
    /** Quando smette di servire (la partita comincia): al più fra 12 ore. */
    expiresAt: z.iso.datetime({ offset: true }).nullable().default(null),
    /** 👥 La partita di un invito: per un invito serve, e prima di mandarlo si ricontrolla che il socio sia ancora adatto. */
    bookingId: z.uuid().nullable().default(null),
})
    .refine((p) => p.kind !== 'invito' || p.bookingId !== null, {
    message: 'Un invito dice a quale partita (bookingId).',
    path: ['bookingId'],
});
export const propostaSchema = z.object({
    id: z.uuid(),
    memberId: z.uuid(),
    /** Per chi approva: il nome intero. A Duet esce col cognome puntato. */
    nome: z.string(),
    kind: z.enum(TIPI_DI_PROPOSTA),
    text: z.string(),
    reason: z.string(),
    /** Quello che il socio aveva scritto, per una risposta. */
    domanda: z.string().nullable(),
    /** 👥 La partita, per un invito. */
    bookingId: z.uuid().nullable(),
    status: z.enum(STATI_DI_PROPOSTA),
    finalText: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
    decidedAt: z.string().nullable(),
    decidedVia: z.enum(['gestionale', 'telegram', 'maglie', 'scadenza']).nullable(),
    /** Chi ha deciso, in parole. */
    decisoDa: z.string().nullable(),
});
export const elencoProposteSchema = z.object({ items: z.array(propostaSchema) });
/** La decisione: approvare (col testo cambiato, se serve) o scartare. */
export const decisioneSchema = z.object({
    azione: z.enum(['approva', 'scarta']),
    text: z.string().trim().min(1).max(1500).optional(),
});
/** ⇢ Dal bot, per un tocco di chi approva su Telegram: la chat dice chi è, mai un numero scritto. */
export const decisioneDalBotSchema = decisioneSchema.extend({ memberId: z.uuid() });
export const esitoDecisioneSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato']),
    reason: z.string().nullable(),
    proposta: propostaSchema.nullable(),
});
// ── Le maglie e chi approva ──────────────────────────────────────────────────
export const MODI_DELLE_MAGLIE = ['chiede', 'sola'];
export const maglieSchema = z.object({
    items: z.array(z.object({ kind: z.enum(TIPI_DI_PROPOSTA), mode: z.enum(MODI_DELLE_MAGLIE) })),
});
export const approvatoriSchema = z.object({
    items: z.array(z.object({ memberId: z.uuid(), nome: z.string(), telegram: z.boolean() })),
});
export const nuovoApprovatoreSchema = z.object({ memberId: z.uuid() });
// ── «🤖 Parla con Duet» (9A) ─────────────────────────────────────────────────
/** ⇢ Dal bot: chi approva ha scritto a Duet. */
export const messaggioPerDuetSchema = z.object({
    memberId: z.uuid(),
    text: z.string().trim().min(1).max(4000),
});
/** ⇢ Da Duet: la risposta a chi approva. */
export const rispostaDiDuetSchema = z.object({
    memberId: z.uuid(),
    text: z.string().trim().min(1).max(4000),
});
export const chiacchieraSchema = z.object({
    items: z.array(z.object({
        id: z.number().int(),
        memberId: z.uuid(),
        nome: z.string(),
        saidBy: z.enum(['amministrazione', 'duet']),
        text: z.string(),
        at: z.string(),
    })),
});
// ── Quello che Duet legge dei soci ───────────────────────────────────────────
/** ⛔ Il minimo: mai telefono, email, borsellino o pagamenti. */
export const sociPerDuetSchema = z.object({
    items: z.array(z.object({
        memberId: z.uuid(),
        nome: z.string(),
        livello: z.number().nullable(),
        sesso: z.enum(['M', 'F']).nullable(),
        fasceInSettimana: z.array(z.enum(FASCE_DEL_GIORNO)),
        fasceNelWeekend: z.array(z.enum(FASCE_DEL_GIORNO)),
        /** Ha detto sì alle partite aperte del suo livello. */
        avvisiPartite: z.boolean(),
        suTelegram: z.boolean(),
    })),
});
//# sourceMappingURL=duet.js.map